import { Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, delay, map, take } from 'rxjs/operators';
import { APIService } from 'src/app/core/api/general.service';
import { GDSs, OptionType } from 'src/app/core/enums/enums';
import { QuoteRequest } from 'src/app/core/models/itinerary.model';
import { LayoutService } from 'src/app/layout/services/layout.service';
import { SettingsService } from 'src/app/settings/services/settings.service';
import { ItineraryService } from '../../search-result/services/itinerary.service';
import { CancellationService } from '../services/cancellation.service';

@Injectable()
export class CancellationGuard implements CanActivate {
  constructor(
    private api: APIService,
    private itineraryService: ItineraryService,
    private service: CancellationService,
    private layoutService: LayoutService,
    private settingsService: SettingsService
  ) {}

  loadCancellationInfo(quoteRequest: QuoteRequest): Observable<boolean> {
    const result = this.api.getCancellationQuote(quoteRequest).pipe(
      delay(0),
      map((cancellationQuote) => {
        this.service.dispatchStoreLoadingPenalties();
        cancellationQuote?.cancellationAdditionalInformation?.requireShowPenalties &&
          this.settingsService
            .getClientSettings()
            .pipe(take(1))
            .subscribe((settings) => {
              if (
                settings &&
                settings?.machineLearningEnabled &&
                (quoteRequest.gds === GDSs[1].code || quoteRequest.gds === GDSs[0].code)
              ) {
                this.getPenaltyTextAI(quoteRequest);
              } else {
                this.getPenaltiesFareBasis(quoteRequest);
              }
            });
        this.layoutService.dispatchChangeBreadcrumb(quoteRequest.pnr, OptionType.Cancellation);
        this.service.dispatchStoreQuoteRequest(quoteRequest);
        this.service.dispatchLoadCancellationSuccess(cancellationQuote);
        return !!cancellationQuote;
      }),
      catchError((response) => {
        this.itineraryService.dispatchGenericError(response.error);
        return this.error();
      })
    );
    return result;
  }

  getPenaltyTextAI(quoteRequest: QuoteRequest) {
    const apiService = this.api;
    const cancellationService = this.service;

    this.api.getPenaltyTextAI(quoteRequest).subscribe({
      next(penaltyMLFareBasis) {
        cancellationService.dispatchStorePenaltiesMLRequest(penaltyMLFareBasis);
      },
      error(err) {
        cancellationService.dispatchErrorMachineLearningParameter();
        apiService.getPenaltiesFareBasis(quoteRequest).subscribe((penaltyFareBasis) => {
          cancellationService.dispatchStorePenaltiesRequest(penaltyFareBasis);
        });
      }
    });
  }

  getPenaltiesFareBasis(quoteRequest: QuoteRequest) {
    this.api.getPenaltiesFareBasis(quoteRequest).subscribe((penaltyFareBasis) => {
      this.service.dispatchStorePenaltiesRequest(penaltyFareBasis);
    });
  }

  error(): Observable<boolean> {
    this.itineraryService.navigateToWithParams('itinerary');
    return of(false);
  }

  canActivate(): Observable<boolean> {
    let quoteRequest: QuoteRequest;
    this.itineraryService
      .getQuoteRequest()
      .pipe(take(1))
      .subscribe((quote) => (quoteRequest = quote));
    return !!quoteRequest ? this.loadCancellationInfo(quoteRequest) : this.error();
  }
}
